import { App } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useUniversityContext } from '@/context';
import useMountedState from '../useMountedState';
import API from '@/services/APIs';

export default function useCreditManagement() {
  const [transferRuleMsg, setTransferRuleMsg] = useState<string>('');
  const [maxTransfer, setMaxTransfer] = useState<number>(0);
  const [minTransfer, setMinTransfer] = useState<number>(0);
  const [transferLoading, setTransferLoading] = useState<boolean>(false);

  const [tokenLoading, setTokenLoading] = useState<boolean>(false);
  const [tokenError, setTokenError] = useState<string>('');

  const [transferModal, setTransferModal] = useState<boolean>(false);
  const [transferSubmitting, setTransferSubmitting] = useState<boolean>(false);

  const { message } = App.useApp();
  const { university } = useUniversityContext();
  const universityURL = university.universityURL;
  const isMounted = useMountedState();

  const getCreditTransferInfo = useCallback(async () => {
    try {
      setTransferLoading(true);
      const response = await API.User.getCreditTransferRuls();
      if (isMounted()) {
        setTransferLoading(false);
        setTransferRuleMsg('');
        setTransferRuleMsg(response.payload.messages.join(''));
        setMinTransfer(response?.payload?.minCreditTransferAmount);
        setMaxTransfer(response?.payload?.maxCreditTransferAmount);
      }
    }
    catch (e) {
      const error = e as HttpErrorResponse;
      if (isMounted()) {
        setTransferLoading(false);
        setTransferRuleMsg(error.messageFa);
      }
    }
  }, [isMounted]);

  const openCreditIncreasePage = useCallback(
    (token: string) => {
      window.open(
        `${universityURL}/j_security_check?loginAsToken=${token}&redirect=/nurture/user/credit/charge/view.rose?callBackAppUrl=${location.origin}/user/wallet`,
        '_self'
      );
    },
    [universityURL]
  );

  const getLoginToken = useCallback(async () => {
    try {
      setTokenLoading(true);
      const response = await API.User.getLoginToken();
      if (isMounted()) {
        setTokenLoading(false);
        setTokenError('');
        openCreditIncreasePage(response.payload.loginAsToken);
      }
    }
    catch (e) {
      const error = e as HttpErrorResponse;
      if (isMounted()) {
        setTokenLoading(false);
        setTokenError(error.messageFa);
      }
    }
  }, [openCreditIncreasePage, isMounted]);

  const transferInitialValues = useCallback((): CreditTransferRequest => {
    return {
      username: '',
      creditTransferAmount: minTransfer,
    };
  }, [minTransfer]);

  const validateCreditTransfer = useCallback(
    (values: CreditTransferRequest) => {
      const errors: { username?: string; creditTransferAmount?: string } = {};
      if (!values.username) errors.username = 'required';
      if (values.creditTransferAmount < minTransfer)
        errors.creditTransferAmount = 'minError';
      if (values.creditTransferAmount > maxTransfer)
        errors.creditTransferAmount = 'maxError';
      return errors;
    },
    [minTransfer, maxTransfer]
  );

  const promptCreditTransferSubmit = useCallback(
    (_creditTransferData: CreditTransferRequest) => {
      setTransferModal(true);
    },
    []
  );

  const handleCreditTransferSubmit = useCallback(
    async (values: CreditTransferRequest, resetForm: Function | null) => {
      try {
        setTransferSubmitting(true);
        const response = await API.User.creditTransfer(values);
        if (isMounted()) {
          setTransferSubmitting(false);
          if (response.code === 10120004) {
            message.error(response.messageFa);
          }
          else {
            message.success(response.messageFa);
            resetForm?.();
          }
        }
      }
      catch (e) {
        const error = e as HttpErrorResponse;
        if (isMounted()) {
          setTransferSubmitting(false);
          message.error(error.messageFa);
        }
      }
    },
    [isMounted, message]
  );

  useEffect(() => {
    // noinspection JSIgnoredPromiseFromCall
    getCreditTransferInfo();
  }, [getCreditTransferInfo]);

  return {
    getCreditTransferInfo,
    transferRuleMsg,
    getLoginToken,
    minTransfer,
    maxTransfer,
    transferLoading,
    tokenLoading,
    tokenError,
    transferInitialValues,
    transferModal,
    setTransferModal,
    promptCreditTransferSubmit,
    validateCreditTransfer,
    handleCreditTransferSubmit,
    transferSubmitting,
  };
}
